import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import '@/styles/lists.css'

/**
 * Поповер под кнопкой (дизайн: `.pop`) — конструктор фильтра, представления, колонки, «⋯».
 *
 * Содержимое монтируется только открытым: меню колонок, представлений и импорта на каждом
 * списке — это десятки узлов и запросы (цели импорта, сегменты), которые никто не
 * открывал. Закрывается кликом мимо и Escape — поповер, который можно закрыть только своей
 * кнопкой, ловит клавиатуру и прячет таблицу.
 */
export function Popover({
  label,
  trigger,
  className,
  buttonClassName,
  align = 'start',
  iconOnly = false,
  open: controlledOpen,
  onOpenChange,
  children,
}: {
  /** Доступное имя кнопки; у кнопки-иконки — единственное. */
  label: string
  trigger: ReactNode
  className?: string
  buttonClassName?: string
  align?: 'start' | 'end'
  /** Кнопка без текста: тогда `label` становится её доступным именем. */
  iconOnly?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactNode | ((close: () => void) => ReactNode)
}) {
  const [ownOpen, setOwnOpen] = useState(false)
  const open = controlledOpen ?? ownOpen
  const setOpen = (next: boolean) => {
    setOwnOpen(next)
    onOpenChange?.(next)
  }
  const root = useRef<HTMLDivElement>(null)
  const id = useId()

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOwnOpen(false)
        onOpenChange?.(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, onOpenChange])

  const close = () => setOpen(false)

  return (
    <div
      className="pop-anchor"
      ref={root}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation()
          close()
        }
      }}
    >
      <button
        type="button"
        className={buttonClassName ?? 'btn btn-ghost btn-sm'}
        aria-label={iconOnly ? label : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
      >
        {trigger}
      </button>
      {open && (
        <div
          id={id}
          className={`pop pop-floating${align === 'end' ? ' pop-end' : ''}${className ? ` ${className}` : ''}`}
          role="dialog"
          aria-label={label}
        >
          {typeof children === 'function' ? children(close) : children}
        </div>
      )}
    </div>
  )
}
